import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { UploadCloud, Download, FileSpreadsheet, FileText, Loader2, CheckCircle2, AlertCircle, FolderOpen, Trash2, History } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/constants";

function ReportStat({ label, value, color }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 text-center">
      <p className={`font-heading text-2xl font-extrabold ${color}`}>{value}</p>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    </div>
  );
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function downloadBlob(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function Sync() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const wsRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [wsUploading, setWsUploading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [report, setReport] = useState(null);

  const { data: logs = [] } = useQuery({ queryKey: ["sync-logs"], queryFn: async () => (await api.get("/sync/logs")).data });
  const { data: wsFiles = [] } = useQuery({ queryKey: ["workspace-files"], queryFn: async () => (await api.get("/workspace/files")).data });

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sync/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      toast.success("Sincronizzazione completata");
      qc.invalidateQueries({ queryKey: ["sync-logs"] });
      qc.invalidateQueries({ queryKey: ["guardie"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadBlob("/guardie/excel-template", "modello_guardie_zoofile.xlsx");
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      await downloadBlob(`/guardie/export/${fmt}`, fmt === "excel" ? "anagrafica_guardie.xlsx" : "anagrafica_guardie.pdf");
    } catch {
      toast.error("Export non riuscito");
    } finally {
      setExporting("");
    }
  };

  const onWorkspaceUpload = async (file) => {
    if (!file) return;
    setWsUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/workspace/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nuova versione caricata");
      qc.invalidateQueries({ queryKey: ["workspace-files"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setWsUploading(false);
      if (wsRef.current) wsRef.current.value = "";
    }
  };

  const downloadWsFile = async (f) => {
    try {
      await downloadBlob(`/workspace/files/${f.id}/download`, f.filename);
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const deleteWsFile = async (f) => {
    if (!window.confirm(`Eliminare la versione ${f.version} (${f.filename})?`)) return;
    try {
      await api.delete(`/workspace/files/${f.id}`);
      toast.success("Versione eliminata");
      qc.invalidateQueries({ queryKey: ["workspace-files"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Sincronizza anagrafica</h1>
        <p className="mt-1 text-sm text-slate-500">
          Importa il foglio Excel. Upsert sul Codice Fiscale: nuovo = crea, esistente = aggiorna, assente = disattivata.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardContent className="p-6">
            <div
              className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 py-10 text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files[0]); }}
            >
              <FileSpreadsheet className="h-10 w-10 text-[#166534]" />
              <p className="mt-3 text-sm font-semibold text-slate-700">Trascina qui il file Excel o selezionalo</p>
              <p className="text-xs text-slate-400">Formato .xlsx — foglio "guardie zoofile"</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden" data-testid="sync-file-input" onChange={(e) => onUpload(e.target.files[0])} />
              <Button className="mt-4 bg-[#14382C] hover:bg-[#0E281F]" disabled={uploading} data-testid="sync-excel-button" onClick={() => fileRef.current?.click()}>
                {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                Sincronizza ora
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex flex-col justify-center gap-3 p-6">
            <p className="text-sm font-semibold text-slate-700">Non hai un file?</p>
            <p className="text-xs text-slate-500">Scarica il modello con le colonne corrette e dati di esempio.</p>
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
              <Download className="mr-2 h-4 w-4" /> Scarica modello Excel
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Export anagrafica */}
      <Card className="mt-6 border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">Esporta anagrafica</h2>
              <p className="mt-1 text-sm text-slate-500">Scarica l'elenco completo delle guardie con dati, decreto e scadenze.</p>
            </div>
            <div className="flex gap-3">
              <Button className="bg-[#166534] hover:bg-[#14532d]" disabled={exporting === "excel"} data-testid="export-excel-button" onClick={() => doExport("excel")}>
                {exporting === "excel" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
                Esporta Excel
              </Button>
              <Button variant="outline" className="border-[#B91C1C] text-[#B91C1C] hover:bg-red-50 hover:text-[#B91C1C]" disabled={exporting === "pdf"} data-testid="export-pdf-button" onClick={() => doExport("pdf")}>
                {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                Esporta PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {report && (
        <Card className="mt-6 border-slate-200 shadow-sm gz-fade-up" data-testid="sync-report">
          <CardContent className="p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
              <CheckCircle2 className="h-5 w-5 text-green-600" /> Report sincronizzazione
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <ReportStat label="Righe totali" value={report.totale_righe} color="text-slate-900" />
              <ReportStat label="Nuovi" value={report.nuovi} color="text-green-600" />
              <ReportStat label="Aggiornati" value={report.aggiornati} color="text-blue-600" />
              <ReportStat label="Disattivati" value={report.disattivati} color="text-slate-500" />
              <ReportStat label="Scartati" value={report.scartati} color="text-red-600" />
              <ReportStat label="Promemoria" value={report.promemoria_inviati ?? 0} color="text-[#D97706]" />
            </div>
            {report.dettagli_scartati?.length > 0 && (
              <div className="mt-5">
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-700">
                  <AlertCircle className="h-4 w-4" /> Righe scartate
                </p>
                <div className="overflow-hidden rounded-lg border border-red-100">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 text-left text-xs font-semibold uppercase text-red-700">
                      <tr><th className="px-3 py-2">Riga</th><th className="px-3 py-2">CF</th><th className="px-3 py-2">Motivo</th></tr>
                    </thead>
                    <tbody className="divide-y divide-red-50">
                      {report.dettagli_scartati.map((d, i) => (
                        <tr key={i}><td className="px-3 py-2">{d.riga}</td><td className="px-3 py-2 font-mono-code text-xs">{d.cf || "—"}</td><td className="px-3 py-2 text-slate-600">{d.motivo}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Area file condivisa */}
      <Card className="mt-6 border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
                <FolderOpen className="h-5 w-5 text-[#166534]" /> File Excel condiviso
              </h2>
              <p className="mt-1 max-w-xl text-sm text-slate-500">
                Area riservata a coordinatori e amministratori. Scarica l'ultima versione, lavoraci con Excel desktop,
                poi ricarica il file aggiornato: ogni upload crea una nuova versione nello storico.
              </p>
            </div>
            <div>
              <input ref={wsRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" className="hidden" data-testid="workspace-file-input" onChange={(e) => onWorkspaceUpload(e.target.files[0])} />
              <Button className="bg-[#14382C] hover:bg-[#0E281F]" disabled={wsUploading} data-testid="workspace-upload-button" onClick={() => wsRef.current?.click()}>
                {wsUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                Carica nuova versione
              </Button>
            </div>
          </div>

          <div className="mt-5" data-testid="workspace-file-list">
            {wsFiles.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-8 text-center text-sm text-slate-400">
                Nessun file condiviso. Carica il primo file Excel master.
              </div>
            ) : (
              <div className="overflow-x-auto gz-scroll">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2">Ver.</th>
                      <th className="py-2">File</th>
                      <th className="py-2">Dimensione</th>
                      <th className="py-2">Caricato da</th>
                      <th className="py-2">Data</th>
                      <th className="py-2 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {wsFiles.map((f, i) => (
                      <tr key={f.id} data-testid={`workspace-file-row-${f.id}`} className={i === 0 ? "bg-emerald-50/40" : ""}>
                        <td className="py-2">
                          <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                            {i === 0 && <History className="h-3.5 w-3.5 text-[#166534]" />}v{f.version}
                          </span>
                        </td>
                        <td className="py-2 font-medium text-slate-800">{f.filename}</td>
                        <td className="py-2 text-slate-500">{fmtSize(f.size)}</td>
                        <td className="py-2 text-slate-500">{f.uploaded_by || "—"}</td>
                        <td className="py-2 text-slate-500">{fmtDateTime(f.uploaded_at)}</td>
                        <td className="py-2">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" data-testid={`workspace-download-${f.id}`} onClick={() => downloadWsFile(f)}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`workspace-delete-${f.id}`} onClick={() => deleteWsFile(f)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6 border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Storico sincronizzazioni</h2>
          {logs.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Nessuna sincronizzazione registrata.</p>
          ) : (
            <div className="overflow-x-auto gz-scroll">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="py-2">Data</th><th className="py-2">Totali</th><th className="py-2">Nuovi</th><th className="py-2">Aggiornati</th><th className="py-2">Disattivati</th><th className="py-2">Scartati</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((l, i) => (
                    <tr key={i}>
                      <td className="py-2 text-slate-600">{fmtDateTime(l.timestamp)}</td>
                      <td className="py-2">{l.totale_righe}</td>
                      <td className="py-2 text-green-600">{l.nuovi}</td>
                      <td className="py-2 text-blue-600">{l.aggiornati}</td>
                      <td className="py-2 text-slate-500">{l.disattivati}</td>
                      <td className="py-2 text-red-600">{l.scartati}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
